import { useToastService } from './useToastService'

export interface AlertPayload {
  text: string
  title?: string
  color: 'error' | 'success' | 'info' | 'warning'
  timeout?: number
  group?: string
  styleClass?: string
  contentStyleClass?: string
}

/**
 * Composable para mostrar alertas de forma centralizada
 * Abstrae la lógica de conversión de colores y configuración del Toast
 *
 * @example
 * ```typescript
 * import { useAlert } from '@wkt/composables/useAlert'
 *
 * const { showAlert } = useAlert()
 *
 * // Mostrar alerta de éxito
 * showAlert({ 
 *   text: 'Operación completada', 
 *   color: 'success' 
 * })
 *
 * // Mostrar alerta de error con timeout personalizado
 * showAlert({ 
 *   text: 'Error al guardar', 
 *   color: 'error',
 *   timeout: 5000
 * })
 * ```
 */
export function useAlert() {
  const toast = useToastService()

  /**
   * Muestra una alerta con el Toast de PrimeVue
   * @param payload - Configuración de la alerta
   */
  const showAlert = (payload: AlertPayload) => {
    const severity = mapColorToSeverity(payload.color)
    const timeout = payload.timeout ?? 3000

    toast.add({
      group: payload.group,
      severity,
      summary: payload.title ?? payload.text,
      detail: payload.title ? payload.text : undefined,
      life: timeout,
      styleClass: payload.styleClass,
      contentStyleClass: payload.contentStyleClass
    })
  }

  /**
   * Mapea los colores usados en Worktime a los severities de PrimeVue
   * @param color - Color a mapear
   * @returns Severity de PrimeVue
   */
  const mapColorToSeverity = (
    color: string
  ): 'error' | 'success' | 'info' | 'warn' => {
    switch (color) {
      case 'error':
        return 'error'
      case 'success':
        return 'success'
      case 'warning':
        return 'warn'
      default:
        return 'info'
    }
  }

  return {
    showAlert
  }
}
